import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../layout/AdminLayout';
import { PageHeader } from '../common/PageHeader';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../ui/dialog';
import { licenseApi, instructorApi, getBackendUrl } from '../../lib/api';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, Search, AlertTriangle, Award, Edit, Eye, Shield, CheckCircle2, Trash2 } from 'lucide-react';

function formatDateDE(d) {
    if (!d) return '-';
    if (d.includes('.')) return d;
    try {
        const date = new Date(d);
        return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return d; }
}

export function LicensesPage() {
    const navigate = useNavigate();
    const [licenses, setLicenses] = useState([]);
    const [instructors, setInstructors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
    const [deactivatingLicenseId, setDeactivatingLicenseId] = useState(null);
    const [deactivationReason, setDeactivationReason] = useState('');
    const [deleteDialogLicense, setDeleteDialogLicense] = useState(null);
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            const [licensesRes, instructorsRes] = await Promise.all([
                licenseApi.getAll(),
                instructorApi.getAll(),
            ]);
            setLicenses(licensesRes.data);
            setInstructors(instructorsRes.data);
        } catch (error) {
            toast.error('Fehler beim Laden der Daten');
        } finally {
            setLoading(false);
        }
    };

    const getInstructorName = (id) => {
        const instructor = instructors.find((i) => i.id === id);
        return instructor ? `${instructor.first_name} ${instructor.last_name}` : 'Unbekannt';
    };

    const isOrphan = (lic) => !lic.instructor_id || !instructors.find((i) => i.id === lic.instructor_id);

    const handleDeleteLicense = async () => {
        if (!deleteDialogLicense) return;
        setDeleteSubmitting(true);
        try {
            await licenseApi.delete(deleteDialogLicense.id);
            toast.success('Lizenz gelöscht');
            setDeleteDialogLicense(null);
            loadData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
        } finally {
            setDeleteSubmitting(false);
        }
    };

    const getLicenseStatus = (lic) => {
        const isActive = lic.is_active !== false;
        const status = lic.status || (isActive ? 'active' : 'deactivated');

        // Check for renewed status first (important for license renewal workflow)
        if (status === 'renewed') return { label: 'Verlängert', class: 'bg-blue-100 text-blue-700', key: 'renewed' };
        if (!isActive || status === 'deactivated') return { label: 'Deaktiviert', class: 'bg-gray-100 text-gray-600', key: 'deactivated' };

        let validDate;
        try {
            validDate = lic.valid_until?.includes('.')
                ? new Date(lic.valid_until.split('.').reverse().join('-'))
                : new Date(lic.valid_until);
        } catch { validDate = new Date(); }
        // Defensive: malformed years like „0026-12-31" should not silently show
        // „Abgelaufen". Surface a clear „Datum prüfen"-state.
        const year = validDate && !isNaN(validDate.getTime()) ? validDate.getFullYear() : null;
        if (year !== null && (year < 1900 || year > 2100)) {
            return { label: 'Datum prüfen', class: 'bg-amber-100 text-amber-800 border border-amber-300', key: 'invalid_date' };
        }
        const now = new Date();
        if (validDate < now) return { label: 'Abgelaufen', class: 'bg-red-100 text-red-700', key: 'expired' };
        if (validDate < new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)) return { label: 'Läuft bald ab', class: 'bg-amber-100 text-amber-700', key: 'expiring' };
        return { label: 'Gültig', class: 'bg-green-100 text-green-700', key: 'valid' };
    };

    const handleToggle = (lic, checked) => {
        if (!checked) {
            setDeactivatingLicenseId(lic.id);
            setDeactivationReason('');
            setDeactivateDialogOpen(true);
        } else {
            handleActivate(lic.id);
        }
    };

    const handleDeactivate = async () => {
        if (!deactivatingLicenseId) return;
        try {
            await licenseApi.deactivate(deactivatingLicenseId, deactivationReason);
            toast.success('Lizenz deaktiviert');
            setDeactivateDialogOpen(false);
            setDeactivatingLicenseId(null);
            setDeactivationReason('');
            loadData();
        } catch { toast.error('Fehler beim Deaktivieren'); }
    };

    const handleActivate = async (licId) => {
        try {
            await licenseApi.activate(licId);
            toast.success('Lizenz aktiviert');
            loadData();
        } catch { toast.error('Fehler beim Aktivieren'); }
    };

    const handleVerify = async (licId) => {
        try {
            await api.post(`/licenses/${licId}/verify`);
            toast.success('Lizenz als geprüft markiert');
            loadData();
        } catch { toast.error('Fehler beim Verifizieren'); }
    };

    const handleMarkExternal = async (licId) => {
        const issuer = window.prompt(
            'Diese Lizenz als Fremdlizenz markieren?\n\n'
            + 'Fremdlizenzen werden im System anerkannt, sind aber NICHT Grundlage für '
            + 'BGPR-Lizenzverlängerungen.\n\n'
            + 'Bitte ausstellende Organisation eintragen (optional):',
            ''
        );
        if (issuer === null) return; // Cancel
        try {
            await api.post(`/licenses/${licId}/mark-external`, { external_issuer: issuer });
            toast.success('Lizenz als Fremdlizenz markiert');
            loadData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Markieren'); }
    };

    const handleUnmarkExternal = async (licId) => {
        if (!window.confirm('Fremdlizenz-Markierung wirklich entfernen? Die Lizenz gilt dann wieder als reguläre BGPR-Lizenz.')) return;
        try {
            await api.post(`/licenses/${licId}/mark-external`, { unmark: true });
            toast.success('Markierung entfernt');
            loadData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    };

    const openDoc = (licId) => {
        // Open the PDF directly in a new tab so the browser honours the
        // Content-Disposition filename (blob-URLs would show a random slug).
        const token = localStorage.getItem('bgpr_token');
        if (!token) { toast.error('Nicht authentifiziert'); return; }
        const url = `${getBackendUrl()}/api/licenses/${licId}/document?token=${encodeURIComponent(token)}`;
        const w = window.open(url, '_blank');
        if (!w) toast.error('Popup blockiert – bitte Popup-Blocker für diese Seite deaktivieren.');
    };

    const pendingReviewLicenses = licenses.filter(
        (l) => l.source === 'self_uploaded' && !l.verified_by_admin
    );

    // Licenses with malformed year (e.g. "0026-12-31" stored from a typo'd
    // date-input). When found, surface an admin healing action.
    const malformedDateLicenses = licenses.filter((l) => {
        const status = getLicenseStatus(l);
        return status.key === 'invalid_date';
    });

    const handleNormalizeDates = async () => {
        if (!window.confirm(
            `${malformedDateLicenses.length} Lizenz(en) mit fehlerhaftem Datum gefunden (z. B. „31. Dezember 26" statt „31. Dezember 2026").\n\n`
            + `Soll das System alle 2-stelligen Jahre automatisch auf 20XX korrigieren?\n`
            + `(z. B. „0026-12-31" → „2026-12-31")`
        )) return;
        try {
            const res = await licenseApi.normalizeDates();
            const d = res.data || {};
            toast.success(d.message || `${d.fixed || 0} Lizenz(en) korrigiert`);
            if (d.errors && d.errors.length) {
                console.warn('normalize-dates errors:', d.errors);
            }
            loadData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Heilung fehlgeschlagen');
        }
    };

    const filteredLicenses = licenses.filter((lic) => {
        const name = getInstructorName(lic.instructor_id).toLowerCase();
        const num = (lic.license_number || '').toLowerCase();
        const type = (lic.license_type || '').toLowerCase();
        const matchesSearch = name.includes(searchTerm.toLowerCase()) ||
            num.includes(searchTerm.toLowerCase()) ||
            type.includes(searchTerm.toLowerCase());
        if (!matchesSearch) return false;
        if (filterStatus === 'all') return true;
        return getLicenseStatus(lic).key === filterStatus;
    });

    return (
        <AdminLayout>
            <PageHeader
                title="Lizenzen"
                description="Übersicht aller Übungsleiter-Lizenzen"
            />

            {/* Prüf-Inbox: selbst-hochgeladene Lizenzen, die noch nicht verifiziert sind */}
            {pendingReviewLicenses.length > 0 && (
                <div className="mb-6 rounded-lg border-2 border-amber-200 bg-amber-50 overflow-hidden" data-testid="license-review-inbox">
                    <div className="px-4 py-3 bg-amber-100 border-b border-amber-200 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Shield className="h-5 w-5 text-amber-700" />
                            <h3 className="font-semibold text-amber-900">
                                {pendingReviewLicenses.length} Lizenz(en) zur Prüfung eingereicht
                            </h3>
                        </div>
                        <span className="text-xs text-amber-700">Von Akademie-Mitgliedern hochgeladene Bestands-Lizenzen</span>
                    </div>
                    <div className="divide-y divide-amber-200">
                        {pendingReviewLicenses.map((l) => (
                            <div key={l.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5 hover:bg-amber-100/50" data-testid={`pending-lic-${l.id}`}>
                                <div className="flex-1 min-w-[240px]">
                                    <div className="text-sm font-semibold text-gray-900">{getInstructorName(l.instructor_id)}</div>
                                    <div className="text-xs text-gray-600">
                                        {l.license_type_name || l.license_type} &middot; Nr. {l.license_number} &middot; Gültig bis: {formatDateDE(l.valid_until)}
                                    </div>
                                </div>
                                {l.document_path && (
                                    <Button variant="outline" size="sm" className="h-8" onClick={() => openDoc(l.id)} data-testid={`review-view-pdf-${l.id}`}>
                                        <Eye className="h-3.5 w-3.5 mr-1" /> PDF ansehen
                                    </Button>
                                )}
                                <Button size="sm" className="h-8 bg-green-600 hover:bg-green-700" onClick={() => handleVerify(l.id)} data-testid={`review-verify-${l.id}`}>
                                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Als geprüft markieren
                                </Button>
                                <Button variant="outline" size="sm" className="h-8 text-amber-700 border-amber-300 hover:bg-amber-100" onClick={() => handleMarkExternal(l.id)} data-testid={`review-mark-external-${l.id}`} title="Lizenz wurde nicht von BGPR ausgestellt – Verlängerung durch BGPR nicht möglich">
                                    Fremdlizenz
                                </Button>
                                <Button variant="outline" size="sm" className="h-8 text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleToggle(l, false)} data-testid={`review-reject-${l.id}`}>
                                    Ablehnen
                                </Button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Datum-Heilung: Lizenzen mit fehlerhaftem Jahr (z.B. „0026-12-31") */}
            {malformedDateLicenses.length > 0 && (
                <div className="mb-6 rounded-lg border-2 border-amber-300 bg-amber-50 p-4 flex flex-wrap items-center gap-3" data-testid="malformed-date-banner">
                    <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0" />
                    <div className="flex-1 min-w-[240px]">
                        <div className="text-sm font-semibold text-amber-900">
                            {malformedDateLicenses.length} Lizenz(en) mit fehlerhaftem Datum erkannt
                        </div>
                        <div className="text-xs text-amber-800">
                            Diese Lizenzen wurden vermutlich mit einer 2-stelligen Jahreszahl gespeichert (z. B. &bdquo;0026-12-31&ldquo; statt &bdquo;2026-12-31&ldquo;). Sie erscheinen aktuell f&auml;lschlich als &bdquo;Abgelaufen&ldquo;.
                        </div>
                    </div>
                    <Button onClick={handleNormalizeDates} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="normalize-dates-btn">
                        Daten automatisch reparieren
                    </Button>
                </div>
            )}

            <div className="mb-6 flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Suchen nach Name, Nummer, Typ..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9"
                        data-testid="search-licenses"
                    />
                </div>
                <div className="flex gap-1.5 flex-wrap">
                    {[
                        { key: 'all', label: 'Alle' },
                        { key: 'valid', label: 'Gültig' },
                        { key: 'expiring', label: 'Bald ablaufend' },
                        { key: 'expired', label: 'Abgelaufen' },
                        { key: 'renewed', label: 'Verlängert' },
                        { key: 'deactivated', label: 'Deaktiviert' },
                    ].map(f => (
                        <Button
                            key={f.key}
                            variant={filterStatus === f.key ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setFilterStatus(f.key)}
                            data-testid={`filter-${f.key}`}
                        >
                            {f.label}
                        </Button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            ) : filteredLicenses.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                    <Award className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>Keine Lizenzen gefunden</p>
                </div>
            ) : (
                <div className="rounded-lg border bg-white overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 border-b">
                            <tr>
                                <th className="text-left px-4 py-3 font-medium">Übungsleiter</th>
                                <th className="text-left px-4 py-3 font-medium">Lizenz</th>
                                <th className="text-left px-4 py-3 font-medium">Lizenznummer</th>
                                <th className="text-left px-4 py-3 font-medium">Ausgestellt</th>
                                <th className="text-left px-4 py-3 font-medium">Gültig bis</th>
                                <th className="text-left px-4 py-3 font-medium">Status</th>
                                <th className="text-center px-4 py-3 font-medium">Aktiv</th>
                                <th className="text-right px-4 py-3 font-medium">Aktionen</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredLicenses.map((lic) => {
                                const status = getLicenseStatus(lic);
                                const isActive = lic.is_active !== false;
                                return (
                                    <React.Fragment key={lic.id}>
                                        <tr
                                            className={`border-b last:border-0 hover:bg-muted/30 ${!isActive ? 'opacity-60' : ''} ${isOrphan(lic) ? 'bg-rose-50/40' : ''}`}
                                            data-testid={`license-row-${lic.id}`}
                                        >
                                            <td className="px-4 py-3 font-medium">
                                                {isOrphan(lic) ? (
                                                    <span className="inline-flex items-center gap-1.5 text-rose-700">
                                                        <AlertTriangle className="h-3.5 w-3.5" />
                                                        Unbekannt
                                                    </span>
                                                ) : (
                                                    getInstructorName(lic.instructor_id)
                                                )}
                                            </td>
                                            <td className="px-4 py-3">{lic.license_type}</td>
                                            <td className="px-4 py-3">
                                                <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{lic.license_number}</code>
                                                {lic.is_external_license && (
                                                    <span
                                                        className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-300"
                                                        title={`Fremdlizenz${lic.external_issuer ? ` (ausgestellt von: ${lic.external_issuer})` : ''} – kann durch BGPR nicht verlängert werden`}
                                                        data-testid={`external-badge-${lic.id}`}
                                                    >
                                                        Fremdlizenz
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">{formatDateDE(lic.issued_date)}</td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-1.5">
                                                    {status.key === 'expiring' && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
                                                    <span className={status.key === 'expired' ? 'line-through text-red-600' : ''}>
                                                        {formatDateDE(lic.valid_until)}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${status.class}`}>
                                                    {status.label}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <Switch
                                                    checked={isActive}
                                                    onCheckedChange={(checked) => handleToggle(lic, checked)}
                                                    data-testid={`toggle-license-${lic.id}`}
                                                />
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="inline-flex items-center gap-1">
                                                    {!isOrphan(lic) && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-7 w-7 p-0"
                                                            title="Zum Profil"
                                                            onClick={() => navigate(`/admin/instructors/${lic.instructor_id}`)}
                                                            data-testid={`view-profile-${lic.id}`}
                                                        >
                                                            <Eye className="h-3.5 w-3.5" />
                                                        </Button>
                                                    )}
                                                    {lic.is_external_license ? (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-7 px-2 text-xs text-amber-700 hover:text-amber-800 hover:bg-amber-50"
                                                            title="Fremdlizenz-Markierung entfernen"
                                                            onClick={() => handleUnmarkExternal(lic.id)}
                                                            data-testid={`unmark-external-${lic.id}`}
                                                        >
                                                            Fremd × 
                                                        </Button>
                                                    ) : (lic.source === 'self_uploaded' && lic.verified_by_admin) && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-7 px-2 text-xs text-muted-foreground hover:text-amber-700 hover:bg-amber-50"
                                                            title="Als Fremdlizenz markieren"
                                                            onClick={() => handleMarkExternal(lic.id)}
                                                            data-testid={`mark-external-${lic.id}`}
                                                        >
                                                            Fremd?
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-7 w-7 p-0 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                                        title={isOrphan(lic) ? 'Verwaiste Lizenz löschen' : 'Lizenz löschen'}
                                                        onClick={() => setDeleteDialogLicense(lic)}
                                                        data-testid={`delete-license-${lic.id}`}
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                        {!isActive && lic.deactivation_reason && (
                                            <tr className="opacity-60" data-testid={`license-reason-${lic.id}`}>
                                                <td colSpan={8} className="px-4 pb-3 pt-0">
                                                    <p className="text-xs text-red-600">Grund: {lic.deactivation_reason}</p>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Deactivation Reason Dialog */}
            <Dialog open={deactivateDialogOpen} onOpenChange={(open) => { if (!open) { setDeactivateDialogOpen(false); setDeactivatingLicenseId(null); } }}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Lizenz deaktivieren</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <p className="text-sm text-muted-foreground">
                            Bitte geben Sie den Grund für die Deaktivierung an:
                        </p>
                        <textarea
                            value={deactivationReason}
                            onChange={(e) => setDeactivationReason(e.target.value)}
                            placeholder="Grund für die Deaktivierung..."
                            className="w-full min-h-[80px] px-3 py-2 rounded-md border bg-background text-sm resize-none"
                            data-testid="deactivation-reason-input"
                        />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => { setDeactivateDialogOpen(false); setDeactivatingLicenseId(null); }}>
                            Abbrechen
                        </Button>
                        <Button variant="destructive" onClick={handleDeactivate} data-testid="confirm-deactivate-button">
                            Deaktivieren
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* Delete License Dialog */}
            <Dialog open={!!deleteDialogLicense} onOpenChange={(o) => !o && setDeleteDialogLicense(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-rose-900">
                            <Trash2 className="h-5 w-5" /> Lizenz endgültig löschen?
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2 text-sm text-muted-foreground">
                        <p>
                            Lizenz <strong>{deleteDialogLicense?.license_number}</strong>
                            {deleteDialogLicense && !isOrphan(deleteDialogLicense) && (
                                <> für <strong>{getInstructorName(deleteDialogLicense.instructor_id)}</strong></>
                            )}
                            {' '}wird unwiderruflich aus der Datenbank entfernt.
                        </p>
                        {deleteDialogLicense && isOrphan(deleteDialogLicense) && (
                            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-rose-900 text-xs">
                                Diese Lizenz ist <strong>verwaist</strong> — das zugehörige Akademie-Mitglied existiert nicht mehr in der Datenbank. Die Löschung ist unbedenklich.
                            </div>
                        )}
                        <p className="text-xs">Vorher ggf. Backup ziehen (Einstellungen → Pre-Deployment-Checkliste).</p>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setDeleteDialogLicense(null)} disabled={deleteSubmitting}>
                            Abbrechen
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDeleteLicense}
                            disabled={deleteSubmitting}
                            data-testid="confirm-delete-license"
                        >
                            {deleteSubmitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
                            Lizenz löschen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
}
